import { useEffect, useState } from "react";
import {
  Check,
  Download,
  Flame,
  HelpCircle,
  Lock,
  LogOut,
  Plus,
  Sparkles,
  Settings,
  Sun,
  Timer,
  Lightbulb,
  NotebookPen,
  Users,
  History,
} from "lucide-react";

const NAV = [
  { label: "Today", icon: Sun },
  { label: "Focus", icon: Timer },
  { label: "Ideas", icon: Lightbulb },
  { label: "Journal", icon: NotebookPen },
  { label: "Contacts", icon: Users },
  { label: "History", icon: History },
];

type Board = {
  name: string;
  moves: number;
  bold: string;
  tasks: { tag: string; text: string; when: string }[];
};

const BOARDS: Board[] = [
  {
    name: "Skylab Studio",
    moves: 128,
    bold: "Pitch the biggest account on retainer",
    tasks: [
      {
        tag: "Outreach",
        text: "Call the three warm leads from last week",
        when: "9:15am, at the desk, before email",
      },
      {
        tag: "Pricing",
        text: "Raise retainer floor to $4k and tell the two oldest clients",
        when: "11am, after the standup",
      },
      { tag: "Product", text: "Ship the export button", when: "2pm, phone face down" },
    ],
  },
  {
    name: "Jitters Coffee",
    moves: 74,
    bold: "Open Saturdays and staff it properly",
    tasks: [
      { tag: "Ops", text: "Re-cut the morning roast schedule", when: "6am, before the doors" },
      {
        tag: "Outreach",
        text: "Walk two sample bags to the hotel down the block",
        when: "10:30am, on the way back",
      },
      {
        tag: "Pricing",
        text: "Test a $6 pour-over on the weekend board",
        when: "Friday close, at the till",
      },
    ],
  },
  {
    name: "Prism Clothing Co",
    moves: 41,
    bold: "Launch the fall line in two boutiques",
    tasks: [
      { tag: "Sales", text: "Pitch the fall catalog to the hotel gift shop", when: "10am, with samples" },
      { tag: "Ops", text: "Move the online restock to Friday morning", when: "Noon, with the warehouse" },
      { tag: "Product", text: "Shoot the new wool coat on a real customer", when: "4pm, before golden hour" },
    ],
  },
  {
    name: "Personal",
    moves: 18,
    bold: "Clear the garage before fall",
    tasks: [
      { tag: "Life", text: "Pickup suit at dry cleaners", when: "Saturday morning, before errands" },
      { tag: "Life", text: "Birthday party Saturday", when: "3pm, grab gift on the way" },
      { tag: "Life", text: "Organize the garage this weekend", when: "Sunday, two hours max" },
    ],
  },
];

// Illustrative "last 14 days" record: 2 = followed through, 1 = logged, 0 = empty
// Realistic streak: mostly strong, with a couple off days to show the app handles them
const DAYS = [2, 2, 2, 1, 2, 2, 0, 2, 2, 1, 2, 2, 2, 2];
const STEP_MS = 1600;

/**
 * Landing hero product surface — a faithful, trimmed-down replica of the real
 * signed-in app (sidebar, business rail, tab bar, Today moves, 14-day record,
 * bold move) that plays a looping "day in the app" animation.
 */
export function HeroConsole() {
  // step 0 = fresh board, 1..3 = each move closed, 4 = hand-off to next board
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setTick(3);
      return;
    }
    const id = window.setInterval(() => setTick((t) => t + 1), STEP_MS);
    return () => window.clearInterval(id);
  }, []);

  const cycle = Math.floor(tick / 5);
  const step = tick % 5;
  const activeIndex = cycle % BOARDS.length;
  const board = BOARDS[activeIndex]!;
  const closed = Math.min(step, 3);

  return (
    <div className="relative mx-auto w-full max-w-5xl px-5 pt-16 pb-20 sm:px-10 sm:pt-28 sm:pb-28">
      <div className="relative">
        {/* Console slab — mirrors the real signed-in layout */}
        <div
          aria-label="A trimmed-down view of the app"
          className="console-tilt vault-panel group relative z-10 overflow-hidden shadow-[0_0_80px_-30px_var(--color-primary)/40]"
        >
          <div className="grid sm:grid-cols-[minmax(0,0.3fr)_minmax(0,1fr)]">
            {/* Sidebar */}
            <div className="hidden flex-col border-r border-border bg-sidebar sm:flex">
              <div className="flex items-center justify-between border-b border-border px-4 py-4">
                <span className="micro-label text-foreground">The Founders App</span>
                <span className="flex gap-1 text-primary">
                  <Lock className="h-3.5 w-3.5" aria-hidden />
                  <LogOut className="h-3.5 w-3.5" aria-hidden />
                </span>
              </div>

              <div className="flex-1 px-3 py-4">
                <p className="micro-label px-2 text-muted-foreground">Businesses</p>
                <div className="mt-3 space-y-px">
                  {BOARDS.map((b, i) => {
                    const active = i === activeIndex;
                    return (
                      <div
                        key={b.name}
                        className={`flex items-baseline gap-3 rounded-sm px-2 py-2.5 text-sm transition-colors duration-500 ${
                          active
                            ? "bg-primary/20 text-primary shadow-[inset_2px_0_0_0_var(--color-primary)]"
                            : "text-muted-foreground"
                        }`}
                      >
                        <span
                          className={`text-[0.625rem] tabular-nums ${
                            active ? "text-primary" : "text-muted-foreground/50"
                          }`}
                        >
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span className="truncate">{b.name}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4 flex items-center gap-2 rounded-sm border border-dashed border-border px-3 py-2.5 text-xs text-muted-foreground">
                  <Plus className="h-3.5 w-3.5" aria-hidden /> Add a business
                </div>
              </div>

              <div className="space-y-3 border-t border-border px-4 py-4">
                <div className="flex items-center justify-between">
                  <span className="micro-label text-muted-foreground">Operator plan</span>
                  <Download className="h-3.5 w-3.5 text-primary" aria-hidden />
                </div>
                <p className="text-xs leading-relaxed text-muted-foreground">
                  "Ordinary things, done consistently, produce extraordinary results."
                </p>
              </div>
            </div>

            {/* Main column */}
            <div className="min-w-0">
              {/* Header */}
              <div className="flex items-center justify-between gap-4 border-b border-border px-5 py-3.5">
                <div className="min-w-0">
                  <p className="micro-label text-muted-foreground">Thursday, September 4</p>
                  <p key={board.name} className="animate-fade-in truncate text-sm font-medium text-foreground">
                    {board.name}
                  </p>
                </div>
                <span className="micro-label hidden text-primary sm:inline">Unlocked</span>
              </div>

              {/* Tab bar */}
              <div className="flex items-center gap-3 overflow-hidden border-b border-border px-5">
                {NAV.map((item, i) => (
                  <span
                    key={item.label}
                    className={`-mb-px flex shrink-0 items-center gap-1.5 border-b-2 py-2.5 text-[0.65rem] font-semibold tracking-[0.14em] uppercase ${
                      i === 0
                        ? "border-primary text-foreground"
                        : "border-transparent text-muted-foreground"
                    }`}
                  >
                    <item.icon className="h-3 w-3" aria-hidden />
                    {item.label}
                  </span>
                ))}
                <span className="-mb-px ml-auto hidden shrink-0 items-center gap-2 py-2.5 text-muted-foreground lg:flex">
                  <Settings className="h-3 w-3" aria-hidden />
                  <HelpCircle className="h-3 w-3" aria-hidden />
                </span>
              </div>

              {/* Body */}
              <div className="space-y-4 p-5">
                {board.tasks.map((m, i) => {
                  const done = i < closed;
                  return (
                    <div
                      key={`${board.name}-${m.text}`}
                      className="rounded-sm border border-border/70 p-4 transition-colors duration-500"
                    >
                      <div className="flex items-start gap-3">
                        <span
                          className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-[2px] border transition-all duration-500 ${
                            done ? "border-primary bg-primary/30 text-primary" : "border-border"
                          }`}
                          aria-hidden
                        >
                          {done && <Check className="animate-scale-in h-3 w-3" />}
                        </span>
                        <div className="min-w-0">
                          <p className="micro-label">
                            Move {String(i + 1).padStart(2, "0")} · {m.tag}
                          </p>
                          <p
                            className={`mt-1 text-sm leading-snug transition-colors duration-500 ${
                              done ? "text-muted-foreground line-through" : "text-foreground"
                            }`}
                          >
                            {m.text}
                          </p>
                          <p className="mt-1.5 text-xs text-muted-foreground">{m.when}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Last 14 days */}
                <div className="rounded-sm border border-border/70 bg-gradient-to-br from-primary/5 to-transparent p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="micro-label flex items-center gap-2">
                        Last 14 days
                        <span className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 text-[0.6rem] font-semibold text-primary">
                          <Flame className="h-3 w-3" aria-hidden /> 12 of 14 days active
                        </span>
                      </p>
                      <p className="mt-1.5 max-w-md text-xs text-muted-foreground">
                        A record, not a scoreboard. Gaps are information, not failure.
                      </p>
                    </div>
                    <div className="hidden text-right sm:block">
                      <p className="text-2xl font-semibold tabular-nums text-foreground">12</p>
                      <p className="text-[0.65rem] text-muted-foreground">of 14 closed</p>
                    </div>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {DAYS.map((state, i) => {
                      const isRecent = i >= DAYS.length - 3;
                      return (
                        <span
                          key={i}
                          className={`flex h-5 w-5 items-center justify-center rounded-full border transition-all duration-500 ${
                            state === 2
                              ? "border-primary/70 bg-primary/40 shadow-[0_0_8px_-3px_var(--color-primary)]"
                              : state === 1
                                ? "border-primary/50 bg-primary/15"
                                : "border-destructive/40 bg-destructive/10"
                          } ${isRecent ? "ring-1 ring-primary/20" : ""}`}
                          title={state === 2 ? "Followed through" : state === 1 ? "Logged, not done" : "No entry"}
                        >
                          {state === 2 && <Check className="h-3 w-3 text-primary" aria-hidden />}
                          {state === 1 && <span className="h-1 w-1 rounded-full bg-primary" aria-hidden />}
                          {state === 0 && <span className="h-0.5 w-0.5 rounded-full bg-destructive/70" aria-hidden />}
                        </span>
                      );
                    })}
                  </div>

                  <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-[0.65rem] text-muted-foreground">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="flex h-3 w-3 items-center justify-center rounded-full border border-primary/70 bg-primary/40">
                        <Check className="h-2 w-2 text-primary" aria-hidden />
                      </span>
                      Followed through
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="flex h-3 w-3 items-center justify-center rounded-full border border-primary/50 bg-primary/15">
                        <span className="h-0.5 w-0.5 rounded-full bg-primary" aria-hidden />
                      </span>
                      Logged, not done
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="flex h-3 w-3 items-center justify-center rounded-full border border-destructive/40 bg-destructive/10">
                        <span className="h-0.5 w-0.5 rounded-full bg-destructive/70" aria-hidden />
                      </span>
                      No entry
                    </span>
                  </div>
                </div>

                {/* Bold move */}
                <div
                  key={board.bold}
                  className="animate-fade-in rounded-sm border border-accent/60 bg-accent/10 p-4 ring-1 ring-accent/30"
                >
                  <p className="micro-label flex items-center gap-2 text-accent">
                    <Sparkles className="h-3.5 w-3.5" aria-hidden /> This month's bold move
                  </p>
                  <p className="mt-2 text-sm text-foreground">{board.bold}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <p className="mt-5 text-center text-[0.7rem] tracking-[0.18em] text-muted-foreground/70 uppercase">
          A trimmed-down look at the real app
        </p>
      </div>
    </div>
  );
}
